/**
 * 文件路径: D:\XXY\MeiShan_Project\frontend\shared\api\menus.ts
 * 菜单 API 封装
 * 
 * 功能：
 * - 菜单列表查询（支持筛选、搜索、分页）
 * - 菜单创建
 * - 菜单详情查询
 * - 菜单更新
 * - 菜单删除
 * - 菜单复制
 * 
 * 依赖：
 * - httpClient: HTTP 请求客户端
 * - Menu, MenuWrite: 自动生成的类型定义
 */

import { httpClient } from './http'
import type { Menu, MenuWrite } from '../generated'

/**
 * 菜单列表查询参数
 */
export interface MenuListParams {
  /** 页码（1-based） */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 运营商ID（多租户过滤） */
  operator_tenant?: string | number
  /** 商户ID */
  merchant?: string | number
  /** 餐厅ID */
  restaurant?: string | number
  /** 目标职场ID（精确匹配，仅返回该职场特定菜单） */
  target_workplace?: string | number
  /** 职场范围过滤：返回通用菜单 + 指定职场特供菜单（⭐ v5.0 新增） */
  target_workplace_scope?: string | number
  /** 标签ID（支持多选，逗号分隔） */
  tags?: string | number | (string | number)[]
  /** 适用餐段code（支持多选，逗号分隔） */
  suitable_meal_segments?: string | string[]
  /** 是否加热 */
  is_heating_enabled?: boolean
  /** 状态（AVAILABLE/UNAVAILABLE） */
  status?: 'AVAILABLE' | 'UNAVAILABLE'
  /** 是否为模板菜单 */
  is_template?: boolean
  /** 搜索关键词（菜单名称、描述、餐厅名称、商户名称） */
  search?: string
  /** 排序字段（支持：name, created_at, updated_at, status） */
  ordering?: string
}

/**
 * 菜单分页响应
 */
export interface MenuListResponse {
  count: number
  next: string | null
  previous: string | null
  results: Menu[]
}

/**
 * 菜单创建/更新数据
 */
export interface MenuCreateData extends Omit<MenuWrite, 'operator_tenant' | 'merchant' | 'restaurant'> {
  operator_tenant?: number
  merchant?: number
  restaurant?: number
}

/**
 * 菜单 API
 */
export const menusApi = {
  /**
   * 获取菜单列表
   * 
   * @param params 查询参数
   * @returns 菜单列表
   * 
   * @example
   * ```typescript
   * // 查询商户的所有菜单
   * const menus = await menusApi.list({ merchant: '123' })
   * 
   * // 查询餐厅的可用菜单
   * const menus = await menusApi.list({
   *   restaurant: '456',
   *   status: 'AVAILABLE'
   * })
   * 
   * // 按标签筛选菜单
   * const menus = await menusApi.list({
   *   tags: ['1', '2']
   * })
   * 
   * // 搜索菜单
   * const menus = await menusApi.list({
   *   search: '午餐'
   * })
   * ```
   */
  async list(params?: MenuListParams): Promise<MenuListResponse> {
    // 处理数组参数
    const processedParams = { ...params }
    const tags = params?.tags
    if (Array.isArray(tags)) {
      processedParams.tags = tags.join(',')
    }
    const segs = params?.suitable_meal_segments
    if (Array.isArray(segs)) {
      processedParams.suitable_meal_segments = segs.join(',')
    }

    const response = await httpClient.get<MenuListResponse>(
      'merchants/menus/',
      { params: processedParams }
    )
    return response
  },

  /**
   * 获取菜单详情
   * 
   * @param id 菜单ID
   * @returns 菜单详情
   * 
   * @example
   * ```typescript
   * const menu = await menusApi.get('1')
   * console.log(menu.name, menu.dishes_detail, menu.tags_detail)
   * ```
   */
  async get(id: string | number): Promise<Menu> {
    const response = await httpClient.get<Menu>(
      `merchants/menus/${id}/`
    )
    return response
  },

  /**
   * 创建菜单
   * 
   * @param data 菜单数据
   * @returns 创建的菜单
   * 
   * @example
   * ```typescript
   * const menu = await menusApi.create({
   *   name: '周三活力午餐',
   *   description: '荤素搭配，营养均衡',
   *   operator_tenant: 1,
   *   merchant: 123,
   *   restaurant: 456,
   *   dishes: ['1', '2', '3'],
   *   tags: ['1', '2'],
   *   suitable_meal_segments: ['WC0003'], // 午餐
   *   is_heating_enabled: true,
   *   status: 'AVAILABLE'
   * })
   * ```
   */
  async create(data: MenuWrite): Promise<Menu> {
    const response = await httpClient.post<Menu>(
      'merchants/menus/',
      data
    )
    return response
  },

  /**
   * 更新菜单
   * 
   * @param id 菜单ID
   * @param data 更新数据（部分字段）
   * @returns 更新后的菜单
   * 
   * @example
   * ```typescript
   * const menu = await menusApi.update('1', {
   *   name: '周三活力午餐（推荐）',
   *   dishes: ['1', '2', '3', '4'], // 新增一道菜
   *   tags: ['1', '2', '3'] // 新增一个标签
   * })
   * ```
   */
  async update(id: string | number, data: Partial<MenuWrite>): Promise<Menu> {
    const response = await httpClient.patch<Menu>(
      `merchants/menus/${id}/`,
      data
    )
    return response
  },

  /**
   * 删除菜单
   * 
   * ⭐ Gap-1-PlanA: 排期与菜单解耦后，菜单可直接删除
   * - 关联排期的 menu FK 会自动置 NULL (on_delete=SET_NULL)
   * - 排期菜品数据存储在 MenuScheduleDish，不受影响
   * 
   * @param id 菜单ID
   */
  async delete(id: string | number): Promise<void> {
    await httpClient.delete(`merchants/menus/${id}/`)
  },

  /**
   * 切换菜单启用/停用状态
   * 
   * POST /api/v1/merchants/menus/{id}/toggle-status/
   * 
   * @param id 菜单ID
   * @returns 更新后的菜单详情
   */
  async toggleStatus(id: string | number): Promise<Menu> {
    const response = await httpClient.post<Menu>(
      `merchants/menus/${id}/toggle-status/`
    )
    return response
  },

  /**
   * 批量获取菜单（不分页）
   * 
   * @param params 查询参数
   * @returns 所有匹配的菜单
   * 
   * @example
   * ```typescript
   * // 获取餐厅的所有可用菜单（用于选择器）
   * const menus = await menusApi.all({
   *   restaurant: '456',
   *   status: 'AVAILABLE'
   * })
   * ```
   */
  async all(params?: Omit<MenuListParams, 'page' | 'page_size'>): Promise<Menu[]> {
    const processedParams = { ...params }
    const tags = params?.tags
    if (Array.isArray(tags)) {
      processedParams.tags = tags.join(',')
    }
    const segs = params?.suitable_meal_segments
    if (Array.isArray(segs)) {
      processedParams.suitable_meal_segments = segs.join(',')
    }

    const response = await httpClient.get<MenuListResponse>(
      'merchants/menus/',
      {
        params: {
          ...processedParams,
          page_size: 1000 // 获取所有数据
        }
      }
    )
    return response.results
  },

  /**
   * 复制菜单
   * 
   * @param id 源菜单ID
   * @param overrides 覆盖数据
   * @returns 新创建的菜单
   * 
   * @example
   * ```typescript
   * // 复制菜单并修改名称
   * const newMenu = await menusApi.copy('1', {
   *   name: '周四活力午餐'
   * })
   * 
   * // 复制菜单到另一个职场
   * const newMenu = await menusApi.copy('1', {
   *   target_workplace: 789
   * })
   * ```
   * 
   * @note
   * 后端暂未实现复制接口，前端先获取详情再创建
   */
  async copy(id: string | number, overrides?: Partial<MenuWrite>): Promise<Menu> {
    // 获取源菜单详情
    const sourceMenu = await this.get(id)

    // 构造新菜单数据
    const newMenuData: MenuWrite = {
      name: `${sourceMenu.name} (副本)`,
      description: sourceMenu.description || undefined,
      operator_tenant: sourceMenu.operator_tenant,
      merchant: sourceMenu.merchant,
      restaurant: sourceMenu.restaurant,
      dishes: sourceMenu.dishes,
      tags: sourceMenu.tags,
      suitable_meal_segments: sourceMenu.suitable_meal_segments,
      target_workplace: sourceMenu.target_workplace || undefined,
      is_heating_enabled: sourceMenu.is_heating_enabled,
      status: sourceMenu.status,
      is_template: false, // 副本默认不是模板
      ...overrides
    }

    // 创建新菜单
    return await this.create(newMenuData)
  },

  /**
   * 检查菜单是否被使用
   * 
   * @param id 菜单ID
   * @returns 使用情况
   * 
   * @example
   * ```typescript
   * const usage = await menusApi.checkUsage('1')
   * if (usage.schedule_count > 0) {
   *   ElMessageBox.confirm(
   *     `该菜单正在被 ${usage.schedule_count} 个排期使用，删除后无法恢复，是否继续？`,
   *     '确认删除'
   *   )
   * }
   * ```
   * 
   * @note
   * 此接口需要后端支持，如果后端未实现，可以省略或在删除时依赖后端错误提示
   */
  async checkUsage(id: string | number): Promise<{ schedule_count: number }> {
    void id
    // TODO: 后端需要实现此接口
    // const response = await httpClient.get<{ schedule_count: number }>(
    //   `merchants/menus/${id}/usage/`
    // )
    // return response

    // 暂时返回模拟数据
    return { schedule_count: 0 }
  },

  /**
   * 获取菜单的排期历史
   * 
   * ⭐ 通过 MenuSchedule API + menu 过滤查询排期历史
   * 
   * @param id 菜单ID
   * @param params 查询参数
   * @returns 排期列表
   */
  async getSchedules(id: string | number, params?: {
    page?: number
    page_size?: number
  }): Promise<{ count: number; results: unknown[] }> {
    const response = await httpClient.get<{ count: number; results: unknown[] }>(
      'merchants/schedules/',
      {
        params: {
          menu: id,
          page: params?.page || 1,
          page_size: params?.page_size || 20,
          ordering: '-created_at',
        }
      }
    )
    return response
  }
}

/**
 * 菜单状态枚举
 */
export enum MenuStatus {
  AVAILABLE = 'AVAILABLE',
  UNAVAILABLE = 'UNAVAILABLE'
}

/**
 * 菜单状态显示配置
 */
export const MENU_STATUS_CONFIG = {
  [MenuStatus.AVAILABLE]: {
    label: '可用',
    type: 'success' as const,
    color: '#67C23A'
  },
  [MenuStatus.UNAVAILABLE]: {
    label: '停用',
    type: 'info' as const,
    color: '#909399'
  }
}

/**
 * 菜单工具函数
 */
export const menuUtils = {
  /**
   * 获取菜单状态显示配置
   * 
   * @param status 状态
   * @returns 显示配置
   */
  getStatusConfig(status: string) {
    return MENU_STATUS_CONFIG[status as MenuStatus] || {
      label: '未知',
      type: 'info' as const,
      color: '#909399'
    }
  },

  /**
   * 格式化菜单名称（添加职场前缀）
   * 
   * @param menu 菜单
   * @returns 格式化后的名称
   */
  formatName(menu: Menu): string {
    if (menu.target_workplace_name) {
      return `【${menu.target_workplace_name}】${menu.name}`
    }
    return menu.name
  },

  /**
   * 检查菜单是否适用于指定餐段
   * 
   * @param menu 菜单
   * @param mealSegmentCode 餐段code
   * @returns 是否适用
   */
  isSuitableForMealSegment(menu: Menu, mealSegmentCode: string): boolean {
    if (!menu.suitable_meal_segments_detail || menu.suitable_meal_segments_detail.length === 0) {
      return true // 未指定适用餐段，默认适用所有餐段
    }
    return menu.suitable_meal_segments_detail.some(
      segment => segment.code === mealSegmentCode
    )
  },

  /**
   * 计算菜单总价
   * 
   * @param menu 菜单
   * @returns 总价（字符串）
   */
  calculateTotalPrice(menu: Menu): string {
    if (!menu.dishes_detail || menu.dishes_detail.length === 0) {
      return '0.00'
    }
    
    // 注意：dishes_detail 中的 price 可能没有，需要后端补充
    // 这里暂时返回 N/A
    return 'N/A'
  },

  /**
   * 生成菜单摘要
   * 
   * @param menu 菜单
   * @returns 摘要文本
   */
  generateSummary(menu: Menu): string {
    const parts: string[] = []
    
    parts.push(`${menu.dishes_detail.length} 道菜`)
    
    if (menu.tags_detail && menu.tags_detail.length > 0) {
      parts.push(`标签: ${menu.tags_detail.map(t => t.name).join('、')}`)
    }
    
    if (menu.suitable_meal_segments_detail && menu.suitable_meal_segments_detail.length > 0) {
      parts.push(`适用: ${menu.suitable_meal_segments_detail.map(s => s.label).join('、')}`)
    }
    
    if (menu.is_heating_enabled) {
      parts.push('需加热')
    }
    
    return parts.join(' | ')
  }
}
